import { useCallback, useMemo, useState } from 'react';
import { Button, ConfigProvider, Input, Modal, Select, Table, Tag, Tooltip } from 'antd';
import heIL from 'antd/locale/he_IL';
import toast from 'react-hot-toast';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBan,
  faMinus,
  faPen,
  faPlus,
  faPrint,
  faWallet,
} from '@fortawesome/free-solid-svg-icons';
import { Money } from '../components/Money';
import { Loader } from '../components/Loader';
import { EmptyState } from '../components/EmptyState';
import ReportingPeriodFilter from '../components/ReportingPeriodFilter';
import { useRegisterRefresh } from '../components/PageActions';
import { PdfDrawer } from '../features/cash/PdfDrawer';
import { LoadDrawer } from '../features/petty-cash/LoadDrawer';
import { ExpenseDrawer } from '../features/petty-cash/ExpenseDrawer';
import { PettyCashFormModal } from '../features/petty-cash/PettyCashFormModal';
import {
  useCancelPettyCashMovement,
  usePettyCash,
  usePettyCashMovements,
} from '../hooks/usePettyCash';
import { useProjectContext } from '../auth/ProjectContext';
import { firstOfMonthIsrael, isoToDisplay, lastOfMonthIsrael } from '../utils/dates';
import type { PettyCashMovement, PettyCashMovementType } from '@deposits/shared';

const TYPE_TAG: Record<PettyCashMovementType, { label: string; color: string }> = {
  load: { label: 'טעינה', color: 'green' },
  expense: { label: 'הוצאה', color: 'orange' },
};

export function PettyCashTab() {
  const { selectedProjectId, selectedProject } = useProjectContext();
  const { data: state, isLoading, isError, refetch } = usePettyCash(selectedProjectId);
  const pettyCash = state?.petty_cash ?? null;

  const [period, setPeriod] = useState(() => ({ from: firstOfMonthIsrael(), to: lastOfMonthIsrael() }));
  const [typeFilter, setTypeFilter] = useState<PettyCashMovementType | null>(null);
  const [staffFilter, setStaffFilter] = useState<number | null>(null);
  const [categoryFilter, setCategoryFilter] = useState<number | null>(null);

  const [formOpen, setFormOpen] = useState(false);
  const [loadOpen, setLoadOpen] = useState(false);
  const [expenseOpen, setExpenseOpen] = useState(false);
  const [pdfMovementId, setPdfMovementId] = useState<number | null>(null);
  const [cancelling, setCancelling] = useState<PettyCashMovement | null>(null);
  const [cancelReason, setCancelReason] = useState('');

  const {
    data: movements = [],
    isFetching: movementsFetching,
    isError: movementsError,
    refetch: refetchMovements,
  } = usePettyCashMovements(pettyCash?.id ?? null, period);

  useRegisterRefresh(
    useCallback(() => {
      refetch();
      refetchMovements();
    }, [refetch, refetchMovements]),
  );

  const cancelMutation = useCancelPettyCashMovement();

  // Filter options come from the loaded period, so they only offer values
  // that actually appear in it.
  const staffOptions = useMemo(() => {
    const seen = new Map<number, string>();
    for (const m of movements) {
      if (m.created_by_user_boxes_id != null) {
        seen.set(m.created_by_user_boxes_id, m.created_by_name ?? `משתמש ${m.created_by_user_boxes_id}`);
      }
    }
    return [...seen].map(([value, label]) => ({ value, label }));
  }, [movements]);
  const categoryOptions = useMemo(() => {
    const seen = new Map<number, string>();
    for (const m of movements) if (m.category_id != null) seen.set(m.category_id, m.category_name ?? '');
    return [...seen].map(([value, label]) => ({ value, label }));
  }, [movements]);

  const visible = useMemo(
    () =>
      movements.filter(
        (m) =>
          (typeFilter == null || m.movement_type === typeFilter) &&
          (staffFilter == null || m.created_by_user_boxes_id === staffFilter) &&
          (categoryFilter == null || m.category_id === categoryFilter),
      ),
    [movements, typeFilter, staffFilter, categoryFilter],
  );

  const submitCancel = () => {
    if (!cancelling) return;
    if (!cancelReason.trim()) {
      toast.error('יש להזין סיבת ביטול');
      return;
    }
    cancelMutation.mutate(
      { id: cancelling.id, reason: cancelReason.trim() },
      {
        onSuccess: () => {
          toast.success('התנועה בוטלה');
          setCancelling(null);
        },
        onError: (err) => toast.error(err.message || 'שגיאה בביטול'),
      },
    );
  };

  if (isLoading) return <Loader fullScreen />;
  if (isError) {
    return <EmptyState title="שגיאה בטעינת הקופה הקטנה" description="נסה שוב או רענן את העמוד" />;
  }

  if (!state || !pettyCash) {
    return (
      <>
        <EmptyState
          title={selectedProject ? `אין קופה קטנה ל${selectedProject.name}` : 'עוד לא נפתחה קופה קטנה'}
          description="פתח קופה קטנה כדי לטעון אליה מזומן מהקבלות ולתעד הוצאות"
          cta={
            <Button type="primary" icon={<FontAwesomeIcon icon={faPlus} />} onClick={() => setFormOpen(true)}>
              פתח קופה קטנה
            </Button>
          }
        />
        <PettyCashFormModal
          open={formOpen}
          editing={null}
          openingBalanceEditable
          projectId={selectedProjectId}
          projectName={selectedProject?.name ?? null}
          onClose={() => setFormOpen(false)}
        />
      </>
    );
  }

  const columns = [
    { title: 'מספר', dataIndex: 'movement_number', width: 70, render: (v: number) => <span dir="ltr">{v}</span> },
    { title: 'תאריך', dataIndex: 'movement_date', width: 100, render: (v: string) => isoToDisplay(v) },
    {
      title: 'סוג',
      dataIndex: 'movement_type',
      width: 80,
      render: (v: PettyCashMovementType) => <Tag color={TYPE_TAG[v].color}>{TYPE_TAG[v].label}</Tag>,
    },
    {
      title: 'סכום',
      dataIndex: 'amount',
      width: 110,
      render: (v: number, m: PettyCashMovement) => (
        <span
          className={
            m.status === 'cancelled'
              ? 'line-through text-secondary-text'
              : m.movement_type === 'load'
                ? 'text-success-green'
                : 'text-error-red'
          }
        >
          <Money amount={m.movement_type === 'load' ? v : -v} />
        </span>
      ),
    },
    {
      title: 'קטגוריה',
      dataIndex: 'category_name',
      width: 120,
      render: (v: string | null) => v ?? '—',
    },
    {
      title: 'תיאור',
      ellipsis: true,
      render: (_: unknown, m: PettyCashMovement) =>
        m.movement_type === 'load' ? `${m.receipt_count} קבלות מזומן` : m.description ?? '—',
    },
    { title: 'הערות', dataIndex: 'notes', ellipsis: true, render: (v: string | null) => v ?? '—' },
    { title: 'ביצע', dataIndex: 'created_by_name', width: 130, ellipsis: true, render: (v: string | null) => v ?? '—' },
    {
      title: 'סטטוס',
      dataIndex: 'status',
      width: 90,
      render: (_: unknown, m: PettyCashMovement) =>
        m.status === 'cancelled' ? (
          <Tooltip title={`${m.cancel_reason ?? ''}${m.cancelled_by_name ? ` · ${m.cancelled_by_name}` : ''}`}>
            <Tag color="red">בוטלה</Tag>
          </Tooltip>
        ) : (
          <Tag color="blue">פעילה</Tag>
        ),
    },
    {
      title: 'פעולות',
      width: 150,
      render: (_: unknown, m: PettyCashMovement) => (
        <div className="flex items-center gap-1">
          {m.movement_type === 'load' && (
            <Button size="small" icon={<FontAwesomeIcon icon={faPrint} />} onClick={() => setPdfMovementId(m.id)}>
              דוח
            </Button>
          )}
          {m.status === 'active' && (
            <Button
              size="small"
              danger
              icon={<FontAwesomeIcon icon={faBan} />}
              onClick={() => {
                setCancelReason('');
                setCancelling(m);
              }}
            >
              בטל
            </Button>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="flex flex-col flex-1 min-h-0">
      <div className="bg-blue-bg border border-border rounded p-4 mb-3 flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-3">
          <FontAwesomeIcon icon={faWallet} className="text-primary" style={{ fontSize: 18 }} />
          <div>
            <div className="text-secondary-text text-small flex items-center gap-2">
              {pettyCash.name}
              <button
                className="text-secondary-text hover:text-main-text"
                onClick={() => setFormOpen(true)}
                aria-label="עריכה"
              >
                <FontAwesomeIcon icon={faPen} style={{ fontSize: 11 }} />
              </button>
            </div>
            <div className="text-heading-2 font-semibold">
              <Money amount={state.balance} />
            </div>
            <div className="text-small text-secondary-text mt-0.5">
              החודש: נטען <Money amount={state.month_loaded} /> · יצא <Money amount={state.month_spent} />
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <div className="text-small text-secondary-text text-end">
            מזומן פנוי בקבלות
            <div className="text-base text-main-text font-medium">
              <Money amount={state.available_cash_total} /> ({state.available_receipt_count} קבלות)
            </div>
          </div>
          <Button
            icon={<FontAwesomeIcon icon={faPlus} />}
            disabled={state.available_receipt_count === 0}
            onClick={() => setLoadOpen(true)}
          >
            טעינה מקבלות
          </Button>
          <Button
            type="primary"
            icon={<FontAwesomeIcon icon={faMinus} />}
            disabled={state.balance <= 0}
            onClick={() => setExpenseOpen(true)}
          >
            הוצאה חדשה
          </Button>
        </div>
      </div>

      <div className="flex items-center gap-2 flex-wrap pb-3">
        <Select
          allowClear
          placeholder="סוג תנועה"
          value={typeFilter ?? undefined}
          onChange={(v) => setTypeFilter(v ?? null)}
          options={[
            { value: 'load', label: 'טעינה' },
            { value: 'expense', label: 'הוצאה' },
          ]}
          style={{ minWidth: 120 }}
        />
        <Select
          allowClear
          placeholder="עובד"
          value={staffFilter ?? undefined}
          onChange={(v) => setStaffFilter(v ?? null)}
          options={staffOptions}
          style={{ minWidth: 140 }}
        />
        <Select
          allowClear
          placeholder="קטגוריה"
          value={categoryFilter ?? undefined}
          onChange={(v) => setCategoryFilter(v ?? null)}
          options={categoryOptions}
          style={{ minWidth: 140 }}
        />
        <div className="flex-1" />
        <ReportingPeriodFilter value={period} onChange={(from, to) => setPeriod({ from, to })} />
      </div>

      <div className="flex-1 min-h-0 overflow-hidden">
        <ConfigProvider locale={heIL} getPopupContainer={() => document.body}>
          <Table<PettyCashMovement>
            rowKey="id"
            columns={columns as any}
            dataSource={visible}
            loading={movementsFetching ? { spinning: true } : false}
            size="small"
            rowClassName={(m) => (m.status === 'cancelled' ? 'opacity-60' : '')}
            pagination={{ defaultPageSize: 50, showSizeChanger: true, size: 'small' }}
            scroll={{ x: 'max-content', y: 'calc(100vh - 440px)' }}
            locale={{
              emptyText: movementsError ? (
                <EmptyState title="שגיאה בטעינת התנועות" description="נסה שוב או רענן את העמוד" />
              ) : (
                <EmptyState title="אין תנועות בטווח" description="טען כסף מהקבלות או רשום הוצאה" />
              ),
            }}
          />
        </ConfigProvider>
      </div>

      <PettyCashFormModal
        open={formOpen}
        editing={pettyCash}
        openingBalanceEditable={state.opening_balance_editable}
        projectId={selectedProjectId}
        projectName={selectedProject?.name ?? null}
        onClose={() => setFormOpen(false)}
      />

      <LoadDrawer
        open={loadOpen}
        pettyCashId={pettyCash.id}
        onClose={() => setLoadOpen(false)}
        onCreated={(id) => {
          setLoadOpen(false);
          setPdfMovementId(id);
        }}
      />

      <ExpenseDrawer
        open={expenseOpen}
        pettyCashId={pettyCash.id}
        balance={state.balance}
        onClose={() => setExpenseOpen(false)}
      />

      <Modal
        open={cancelling != null}
        title={
          cancelling
            ? `ביטול ${TYPE_TAG[cancelling.movement_type].label} מספר ${cancelling.movement_number}`
            : ''
        }
        okText="בטל תנועה"
        okButtonProps={{ danger: true }}
        cancelText="חזרה"
        onOk={submitCancel}
        onCancel={() => setCancelling(null)}
        confirmLoading={cancelMutation.isPending}
      >
        <div className="flex flex-col gap-2 pt-2">
          <div className="text-small text-secondary-text">
            התנועה תישאר ברשימה ותסומן כמבוטלת.
            {cancelling?.movement_type === 'load' && ' הקבלות יחזרו להיות פנויות להפקדה.'}
          </div>
          <div className="text-small text-main-text">
            סיבת הביטול <span className="text-error-red">*</span>
          </div>
          <Input.TextArea
            value={cancelReason}
            onChange={(e) => setCancelReason(e.target.value)}
            maxLength={500}
            rows={2}
          />
        </div>
      </Modal>

      <PdfDrawer
        open={pdfMovementId !== null}
        title="טעינת קופה קטנה"
        blobPath={pdfMovementId !== null ? `/petty-cash/movements/${pdfMovementId}/pdf-blob` : null}
        downloadName={`petty-cash-load-${pdfMovementId ?? ''}.pdf`}
        onClose={() => setPdfMovementId(null)}
      />
    </div>
  );
}
